import {
  Attribute,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  Optional,
  Self,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'xten-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextComponent),
      multi: true,
    },
  ],
})
export class InputTextComponent implements OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() required: boolean | null = null;
  @Input() type: 'text' | 'password' | 'number' = 'text';
  @Input() widthPx: number | null = 300;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() showTogglePassword = false;
  @Input() showCopy = false;
  @Input() readonly = false;

  value: string = '';
  disabled = false;
  hidePassword = true;
  copied = false;
  copyHint = '';

  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Optional() private controlContainer: ControlContainer,
    @Attribute('formControlName') public controlName: string,
    private cdr: ChangeDetectorRef,
  ) {}

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(v: string): void {
    this.value = v ?? '';
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateValue(v: string) {
    this.value = v;
    this.onChange(v);
  }

  markTouched() {
    this.onTouched();
  }

  get isRequired(): boolean {
    if (this.required !== null) return this.required;

    const c: any = this.control;
    if (!c) return false;

    if (typeof c.hasValidator === 'function') {
      return c.hasValidator(Validators.required);
    }

    if (!c.validator) return false;
    const probe = new FormControl(null);
    probe.setValidators(c.validator);
    probe.updateValueAndValidity({ emitEvent: false });
    return !!probe.errors?.['required'];
  }

  get control(): AbstractControl | null {
    return this.controlContainer?.control?.get(this.controlName) ?? null;
  }

  get showError(): boolean {
    const c = this.control;
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  get pending(): boolean {
    return this.control?.pending ?? false;
  }

  get errorMessage(): string {
    const c = this.control;
    if (!c?.errors) return '';
    if (c.errors['required']) return `กรุณากรอก${this.label}`;
    if (c.errors['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (c.errors['maxlength'])
      return `ยาวเกิน ${c.errors['maxlength'].requiredLength} ตัวอักษร`;
    return 'ข้อมูลไม่ถูกต้อง';
  }

  get inputType(): 'text' | 'password' | 'number' {
    if (this.type !== 'password') return this.type;
    if (!this.showTogglePassword) return 'password';
    return this.hidePassword ? 'password' : 'text';
  }

  togglePassword() {
    this.hidePassword = !this.hidePassword;
  }

  async copyValue() {
    if (this.disabled) return;

    const text = this.value ?? '';
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    this.copied = true;
    this.copyHint = 'คัดลอกแล้ว';
    this.cdr.markForCheck();

    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      this.copied = false;
      this.copyHint = '';
      this.copyTimer = null;
      this.cdr.markForCheck();
    }, 1400);
  }

  ngOnDestroy(): void {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  }
}
